import { createSelector } from 'reselect';
import { isEmpty } from 'lodash-es';

import localStorageService from 'services/localStorage';
import {
  isMobileNotSdkSelector,
  getSdkParams,
  isSdkSelector,
  getPreviewConfig
} from 'redux/selectors/global';
import { getFormState } from 'redux/selectors/form';
import {
  canMakeAnotherPayment,
  showBillingAddress,
  getDynamicStateLabel as getDynamicStateLabelFunction,
  calcCurrencySelectionPaymentMethodTypeCurrencies,
  checkIsInProgressPayment,
  checkIsCompletedPayment
} from 'utilities';
import {
  BILLING_ADDRESS_COLLECT_VALUES,
  FIXED_SIDE,
  PAYMENT_CATEGORIES,
  CHECKOUT_FORM_NAME,
  PMT_FIELD_NAME,
  MANDATORY_CARD_FIELDS
} from 'consts';
import { REQ_STATUSES } from 'redux/constants';
import merge from 'lodash-es/merge';

export const getCheckoutState = state => state.checkout;

export const getCheckoutForm = createSelector(
  [getFormState],
  state => state[CHECKOUT_FORM_NAME] || {}
);

export const getCheckoutPage = createSelector(
  [getCheckoutState],
  state => state.checkoutPage
);

export const getOriginalPaymentObjectData = createSelector(
  [getCheckoutState],
  state => state.originalPaymentObject || {}
);

export const getCheckoutPageData = createSelector(
  [getCheckoutPage, getPreviewConfig],
  (state, previewConfig) => {
    const { checkoutPage: checkoutPageFromPreviewConfig } = previewConfig || {};
    const result = !isEmpty(state.data)
      ? merge({}, state.data, checkoutPageFromPreviewConfig)
      : state.data;
    return result;
  }
);

export const getCheckoutPageLoading = createSelector(
  [getCheckoutPage],
  state => state.loading
);

export const getCheckoutFeatureFlagSelector = createSelector(
  [getCheckoutPageData],
  state => state?.feature_flags || {}
);

export const getKwpAdditionalData = createSelector(
  [getCheckoutPageData],
  state => state?.kwp_additional_data || {}
);

export const getKwpValitorAdditionalDataSelector = createSelector(
  [getCheckoutPageData],
  state => state?.kwp_valitor_additional_data || {}
);

export const getSelectedType = createSelector(
  [isMobileNotSdkSelector, getCheckoutState, getCheckoutFeatureFlagSelector],
  (isMobileMotSdk, state, featureFlags) => {
    if (!isMobileMotSdk || !isEmpty(state.selectedType)) return state.selectedType;
    const { card_abstraction: cardAbstraction } = featureFlags;
    const { selectedCategory = '' } = state;

    if (cardAbstraction && selectedCategory === PAYMENT_CATEGORIES.card) return {};

    return (
      localStorageService.getParsedItem(
        `checkoutMobileType_${state.checkoutPage.data.id}`
      ) ?? {}
    );
  }
);

export const getSelectedCategory = createSelector(
  [isMobileNotSdkSelector, getCheckoutState],
  (isMobileMotSdk, state) => {
    if (!isMobileMotSdk || state.selectedCategory) return state.selectedCategory;

    return (
      localStorageService.getItem(
        `checkoutMobileCategory_${state.checkoutPage.data.id}`
      ) ?? ''
    );
  }
);

export const getRequiredFieldsLoading = createSelector(
  [getCheckoutState],
  state => state.requiredFieldsLoading
);

export const getConditionalFields = createSelector([getCheckoutState], state => {
  if (!isEmpty(state.conditionalFields)) {
    return state.conditionalFields;
  }

  return (
    localStorageService.getParsedItem(
      `checkoutMobileConditionalFields_${state.checkoutPage.data.id}`
    ) ?? {}
  );
});

export const getRequiredFields = createSelector([getCheckoutState], state => {
  if (!isEmpty(state.requiredFields)) {
    return state.requiredFields;
  }

  return (
    localStorageService.getParsedItem(
      `checkoutMobileRequiredFields_${state.checkoutPage.data.id}`
    ) ?? []
  );
});

export const getPmOptions = createSelector([getCheckoutState], state => {
  if (!isEmpty(state.pmOptions)) {
    return state.pmOptions;
  }

  return (
    localStorageService.getParsedItem(
      `checkoutMobilePmOptions_${state.checkoutPage.data.id}`
    ) ?? []
  );
});

export const getCustomerRequiredFields = createSelector([getCheckoutState], state => {
  if (!isEmpty(state.customerRequiredFields)) {
    return state.customerRequiredFields;
  }

  return (
    localStorageService.getParsedItem(
      `checkoutMobileCustomerRequiredFields_${state.checkoutPage.data.id}`
    ) || {}
  );
});

export const getCheckoutPageToken = createSelector(
  [getCheckoutPageData],
  state => state.id
);

export const getCheckoutPaymentData = createSelector(
  [getCheckoutPageData],
  state => state.payment
);

export const getCheckoutCustomerData = createSelector(
  [getCheckoutPageData],
  state => state.customer_data
);

export const getCheckoutCardPaymentMethods = createSelector(
  [getCheckoutPageData],
  state => state.customer_payment_methods
);

export const getCheckoutPaymentFxMetadata = createSelector(
  [getCheckoutPageData],
  state => state.payment_fx_metadata
);

export const getCheckoutMerchantFields = createSelector(
  [getCheckoutPageData],
  state => state.merchant_fields
);

export const getCheckoutPaymentMethodRequiredFields = createSelector(
  [getCheckoutPageData],
  state => state.payment_method_required_fields
);

export const isRequiredCardCvv = createSelector(
  [getCheckoutPageData],
  state => state.require_card_cvv
);

export const getCartItems = createSelector(
  [getCheckoutPageData],
  state => state.cart_items
);

export const getPmImages = createSelector(
  [getCheckoutPageData],
  state => state.pms_images
);

export const getCheckoutPageCountry = createSelector(
  [getCheckoutPageData],
  state => state.country
);

export const getPaymentMethodTypes = createSelector(
  [getCheckoutPageData],
  state => state.payment_method_types
);

export const getSupportedPaymentMethodTypes = createSelector(
  [getCheckoutPageData],
  state => state.supported_payment_method_types
);

export const getCustomElementsObj = createSelector(
  [getCheckoutPageData],
  state => state.custom_elements || {}
);

export const isDisplayMerchantCurrencyOnly = createSelector(
  [getCustomElementsObj, getCheckoutPageData],
  (customElements, checkoutPageData) => {
    const merchantPassedProp = !!customElements.merchant_currency_only;
    const fixedSide = checkoutPageData?.payment?.fixed_side;
    const fixedSideBuy = fixedSide === FIXED_SIDE.BUY;
    return merchantPassedProp && fixedSideBuy;
  }
);

export const getPaymentFeesDisplay = createSelector(
  [getCustomElementsObj],
  state => !(state.payment_fees_display === false)
);

export const isDisplayPaymentFees = createSelector(
  [getPaymentFeesDisplay, isDisplayMerchantCurrencyOnly],
  (displayFees, merchantCurrencyOnly) => {
    if (merchantCurrencyOnly) return false;
    return displayFees;
  }
);

const isSaveCardCheckedSdk = createSelector(
  [getSdkParams],
  state => state.saveCardDefault
);

export const hideCartItemsSelector = createSelector(
  [getSdkParams],
  state => state.hideCartItems
);

export const hideOrderDetailsSelector = createSelector(
  [getSdkParams],
  state => state.hideOrderDetails
);

export const hideSubmitButtonSelector = createSelector(
  [isSdkSelector, getSdkParams],
  (isSdk, sdkParams) => !!isSdk && !!sdkParams?.hideSubmitButton
);

const isSaveCardCheckedCustomElements = createSelector(
  [getCustomElementsObj],
  state => !!state.save_card_default
);

export const isSaveCardChecked = createSelector(
  [isSaveCardCheckedSdk, isSaveCardCheckedCustomElements],
  (saveCardCheckedSdk, saveCardCheckedCustomElements) =>
    saveCardCheckedSdk || saveCardCheckedCustomElements
);

const hideSaveCardSdk = createSelector([getSdkParams], state => state.saveCardDefault);

const hideSaveCard = createSelector(
  [getCustomElementsObj],
  state => !!state.hide_save_card
);

export const isSaveCardHidden = createSelector(
  [hideSaveCardSdk, hideSaveCard],
  (savedCardIsHiddenSdk, savedCardIsHidden) => savedCardIsHiddenSdk || savedCardIsHidden
);

const getDisplayBillingAddress = createSelector([getCustomElementsObj], state => {
  const { billing_address_collect: billingAddressCollect } = state;

  if (
    typeof billingAddressCollect === 'string' &&
    Object.keys(BILLING_ADDRESS_COLLECT_VALUES).includes(billingAddressCollect)
  ) {
    return BILLING_ADDRESS_COLLECT_VALUES[billingAddressCollect];
  }

  return !!billingAddressCollect;
});

export const getCustomerAddresses = createSelector(
  [getCheckoutPageData],
  state => state.customer_data?.addresses || []
);

export const getDynamicStateLabel = createSelector(
  [getCustomerAddresses, getCheckoutPageCountry],
  getDynamicStateLabelFunction
);

export const isDisplayBillingAddress = createSelector(
  [getDisplayBillingAddress, getCheckoutCustomerData],
  showBillingAddress
);
export const isDemoDisplayBillingAddress = createSelector(
  [getPreviewConfig],
  previewConfig => {
    const { checkoutPage, isDemo } = previewConfig || {};
    const { demo_billing_address_collect: demoBillingAddressCollect } =
      checkoutPage || {};

    return isDemo && demoBillingAddressCollect;
  }
);

export const isDisplayPaymentDescription = createSelector(
  [getCustomElementsObj],
  state => !!state.display_description
);

export const getCardHolderName = createSelector(
  [getCustomElementsObj],
  state => state.cardholder_name || ''
);

export const getCustomElementsRequiredCustomerFields = createSelector(
  [getCustomElementsObj],
  state => state.required_customer_fields || []
);

export const getSelectedCurrency = createSelector(
  [getCheckoutState, getPreviewConfig],
  (state, previewConfig) => {
    const { checkoutPage } = previewConfig || {};
    const { payment_fx_metadata: paymentFxMetadata } = checkoutPage || {};
    const { currency_code: previewSelectedCurrency } = paymentFxMetadata || {};
    return previewSelectedCurrency || state.selectedCurrency;
  }
);

const getCreatingPayment = createSelector(
  [getCheckoutState],
  state => state.creatingPayment
);

export const isSubmittingPayment = createSelector(
  [getCreatingPayment],
  state => state.loading
);

const getAuthorizationStatusSelector = createSelector(
  [getCheckoutState],
  state => state.authorizationStatus
);

export const getCreatePaymentButtonStatus = createSelector(
  [getCheckoutPageData, getAuthorizationStatusSelector, isSubmittingPayment],
  (checkoutPageData, authorizationStatus, submittingPayment) => {
    const isAuthorizationInProgress = authorizationStatus === REQ_STATUSES.PENDING;
    const isAuthorizationSuccess = authorizationStatus === REQ_STATUSES.RESOLVED;
    const isInProgressPayment = checkIsInProgressPayment(checkoutPageData);
    const isCompletedPayment = checkIsCompletedPayment(checkoutPageData);
    const isActionInProgress = submittingPayment || isAuthorizationInProgress;
    const isButtonLoading = isInProgressPayment || isActionInProgress;
    const isButtonSuccess = isCompletedPayment || isAuthorizationSuccess;

    return {
      loading: isButtonLoading,
      success: isButtonSuccess
    };
  }
);

export const getCheckoutCustomErrorsSelector = createSelector(
  [getCheckoutState],
  state => state?.customErrors || {}
);

export const isKortaSelector = createSelector(
  [getKwpAdditionalData],
  kwpAdditionalData => {
    return !isEmpty(kwpAdditionalData) ? Boolean(kwpAdditionalData.is_korta) : false;
  }
);

export const isValitorSelector = createSelector(
  [getKwpValitorAdditionalDataSelector],
  kwpValitorAdditionalData => {
    return !isEmpty(kwpValitorAdditionalData);
  }
);

export const getCustomerBankPaymentMethodsSelector = createSelector(
  [getCheckoutPageData],
  state => state?.bank_redirect_customer_payment_methods || []
);

export const getBankListAvailableSelector = createSelector(
  [getSelectedCategory, getCustomerBankPaymentMethodsSelector],
  (category, banks) =>
    banks.length > 0 &&
    [PAYMENT_CATEGORIES.bank, PAYMENT_CATEGORIES.bank_redirect].includes(category)
);

export const isCurrencySelectionShown = createSelector(
  [
    getCustomElementsObj,
    getCheckoutPageData,
    getOriginalPaymentObjectData,
    getSelectedType,
    getSelectedCategory,
    getCheckoutForm
  ],
  (
    customElements,
    checkoutPage,
    originalPaymentObject,
    selectedType,
    selectedCategory,
    checkoutForm
  ) => {
    if (customElements.merchant_currency_only) return false;
    if (!selectedCategory) return false;
    if (isEmpty(checkoutForm?.values)) return false;

    const {
      payment: paymentData,
      isDemo,
      payments_metadata: paymentsMetadata,
      payment_method_types: paymentMethodTypes
    } = checkoutPage;
    const paymentObj = !isEmpty(originalPaymentObject)
      ? originalPaymentObject
      : paymentData;
    const {
      currency_code: currencyCode,
      merchant_requested_currency: merchantRequestedCurrency,
      id,
      status: paymentStatus
    } = paymentObj;

    const pmtType = checkoutForm.values[PMT_FIELD_NAME] || selectedType?.type;
    const formCardNumberValue = checkoutForm.values[MANDATORY_CARD_FIELDS.number];

    const relevantPmts = paymentMethodTypes.find(
      pmtCategory => pmtCategory.category === selectedCategory
    );
    const paymentMethodTypeObject = relevantPmts?.types?.find(
      pmt => pmt.type === pmtType
    );

    const cardPaymentMethodTypes =
      paymentMethodTypes.find(
        pmtCategory => pmtCategory.category === PAYMENT_CATEGORIES.card
      )?.types || [];

    const paymentWasProcessed =
      id &&
      !canMakeAnotherPayment({
        paymentStatus,
        paymentsMetadata
      });
    if (isDemo || paymentWasProcessed) return false;
    if (!currencyCode || !merchantRequestedCurrency) return false;

    const supportedCurrencies =
      paymentMethodTypeObject?.supported_currencies ||
      calcCurrencySelectionPaymentMethodTypeCurrencies({
        cardNumber: formCardNumberValue,
        cardPaymentMethodTypes
      });
    const currenciesToSupport = [merchantRequestedCurrency, currencyCode].filter(Boolean);

    if (isEmpty(supportedCurrencies)) return false;
    const allCurrenciesSupported = currenciesToSupport.every(currency =>
      supportedCurrencies.includes(currency)
    );
    if (!allCurrenciesSupported) return false;
    if (paymentMethodTypeObject?.max_amount || paymentMethodTypeObject?.min_amount)
      return false;

    return !!customElements.dynamic_currency_conversion;
  }
);

export const getSelectedPmtType = createSelector(
  [getCheckoutForm, getSelectedType],
  (checkoutForm, selectedType) => {
    return checkoutForm?.values?.[PMT_FIELD_NAME] || selectedType?.type;
  }
);

export const getAft = createSelector(
  [getCheckoutPageData],
  state => state?.account_funding_transaction || {}
);

export const getSupportedAftPmts = createSelector(
  [getAft],
  state => state?.payment_method_types || []
);

export const isAftFlow = createSelector(
  [getSupportedAftPmts, getSelectedPmtType],
  (supportedAftPmts, selectedPmtType) => {
    return supportedAftPmts?.includes(selectedPmtType);
  }
);

export const hasMerchantTriggeredSdkSubmitSelector = createSelector(
  [getCheckoutState],
  checkoutState => {
    return checkoutState?.hasMerchantTriggeredSdkSubmit || false;
  }
);

export const isCheckoutPageDataDirty = createSelector(
  [getCheckoutPageData],
  state => state.isDirty
);

export const getRecurrenceType = createSelector(
  [getCheckoutPageData],
  state => state.recurrence_type
);

export const isCheckoutPageHasOnePMT = createSelector(
  [getCheckoutPageData],
  checkoutPage => {
    const {
      payment_method_types: paymentMethodTypes,
      payment_method_type: paymentMethodType
    } = checkoutPage || {};

    const paymentMethodTypesList =
      paymentMethodTypes?.find(
        pmtCategory => pmtCategory.category === PAYMENT_CATEGORIES.card
      )?.types || [];

    return paymentMethodTypesList?.length === 1 || !!paymentMethodType;
  }
);
